import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as catalogApi from "../../api/catalogApi";
import * as songsApi from "../../api/songsApi";
import { failureMessage } from "../../api/client";
import { endSession, selectSong } from "../../actions/sessionActions";
import { queueSongNext, queueSongSung, setSongSung } from "../../actions/queueActions";
import { acceptRequest, dismissRequest } from "../../actions/requestsActions";
import { addSongToLibrary } from "../../actions/libraryActions";
import { loadTopSongs } from "../../actions/catalogActions";
import { showInfo, showNotice } from "../../actions/noticeActions";
import {
  selectCurrentVerse,
  selectCurrentVerseIndex,
  selectLiveSetName,
  selectQueuePosition,
  selectQueueSongs,
  selectRequests,
  selectUpNextSong,
} from "../../selectors";
import { verseLabel } from "../../lib/lyrics";
import { transposeNote } from "../../lib/chords";
import { parseSections, sectionsToText } from "../../lib/sections";
import SongEditorPage from "../songs/SongEditorPage";
import SongImportPage from "../songs/SongImportPage";
import PreviewRow from "./PreviewRow";
import SongPreview from "./SongPreview";

const OPEN_SHARE = 0.78; // of the viewport height, the sheet's open height...
const OPEN_MAX = 720; // ...at most this (matches the stylesheet)
const COLLAPSED_FALLBACK = 118; // the closed sheet's height before it was ever measured
const TAP_TOLERANCE = 6; // px of travel that still counts as a tap on the handle

const ordinal = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th"}`;

/** The bar at the top of the sheet: a tap opens or closes it, a drag resizes it. */
const Handle = ({ drag, label }) => (
  <button type="button" className="sheet-handle" aria-label={label} {...drag}>
    <span />
  </button>
);

/** The collapsed sheet: where the set stands, and what comes next. */
const ClosedSheet = ({ drag, onAdd, sheetRef }) => {
  const dispatch = useDispatch();
  const code = useSelector((state) => state.session.Code);
  const current = useSelector((state) => state.session.SongId);
  const setName = useSelector(selectLiveSetName);
  const { position, count } = useSelector(selectQueuePosition);
  const upNext = useSelector(selectUpNextSong);

  const goNext = () => {
    if (!upNext) return;
    if (current) dispatch(queueSongSung(current, true));
    dispatch(selectSong(code, upNext.Id, 0));
  };

  return (
    <div ref={sheetRef} className="sheet" data-screen-label="Host sheet · closed">
      <Handle drag={drag} label="Open the set" />
      <div className="sheet-strip">
        <button type="button" className="sheet-strip-text" {...drag}>
          <span className="sheet-kicker">
            {setName} · {count ? `${position || 0} of ${count}` : "empty"}
          </span>
          <span className="sheet-strip-title">{upNext ? `Next: ${upNext.Title}` : count ? "Last song — pick what follows" : "Add the first song"}</span>
        </button>
        {upNext ? (
          <button type="button" className="btn btn-round btn-md btn-primary" onClick={goNext} aria-label={`Next song: ${upNext.Title}`}>
            →
          </button>
        ) : (
          <button type="button" className="btn btn-round btn-md btn-outline" onClick={onAdd} aria-label="Add a song">
            ＋
          </button>
        )}
      </div>
    </div>
  );
};

/** The set strip: every song, the live one coloured, skips, requests, hearts, the way out. */
const SetMode = ({ drag, onAdd, onPreview }) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const songs = useSelector(selectQueueSongs);
  const requests = useSelector(selectRequests);
  const setName = useSelector(selectLiveSetName);
  const { position, count } = useSelector(selectQueuePosition);
  const verse = useSelector(selectCurrentVerse);
  const verseIndex = useSelector(selectCurrentVerseIndex);
  const [ending, setEnding] = useState(false);

  const end = async () => {
    if (!window.confirm("End the session for everyone?")) return;
    setEnding(true);
    await dispatch(endSession(session.Code));
    setEnding(false);
  };

  const requestKicker = (request) => {
    const parts = ["Request"];
    if (request.RequestedBy) parts.push(request.RequestedBy);
    if (request.Votes > 1) parts.push(`${request.Votes} asking`);
    return parts.join(" · ");
  };

  return (
    <>
      <Handle drag={drag} label="Close the set" />
      <div className="sheet-head" style={{ alignItems: "baseline" }}>
        <span className="sheet-title">{setName}</span>
        <span className="sheet-pos">{count ? `${position || 0} of ${count}` : "empty"}</span>
      </div>
      <div className="sheet-body">
        {songs.map((song, index) => {
          const on = song.Id === session.SongId;
          const skipped = Boolean(song.Sung) && !on;
          const key = song.Key ? transposeNote(song.Key, song.Transpose || 0) : "";
          return (
            <div key={song.Id} className={`sheet-row${on ? " on" : skipped ? " skipped" : ""}`}>
              <span className="row-num">{index + 1}</span>
              <button type="button" className="sheet-row-title" onClick={() => !on && dispatch(selectSong(session.Code, song.Id, 0))} disabled={on}>
                {song.Title}
              </button>
              {key && <span className="sheet-row-key">{key}</span>}
              {on ? (
                <span className="sheet-row-mark">{verseLabel(verse, verseIndex)}</span>
              ) : (
                <button type="button" className="sheet-row-mark" onClick={() => dispatch(setSongSung(session.Code, song.Id, !skipped))}>
                  {skipped ? "Skipped · undo" : "Skip"}
                </button>
              )}
            </div>
          );
        })}
        {songs.length === 0 && <div className="sheet-note sheet-note-lg">No songs yet. Add one below, or accept a request.</div>}
        {requests.map((request) => (
          <div key={request.Id} className="sheet-row-dashed">
            <PreviewRow kicker={requestKicker(request)} title={request.SongTitle} onPreview={(state) => onPreview(state ? { songId: request.SongId, title: request.SongTitle, held: state.held } : null)} />
            <button type="button" className="btn btn-xs btn-primary" onClick={() => dispatch(acceptRequest(session.Code, request, false))} aria-label={`Accept the request for ${request.SongTitle}`}>
              Accept
            </button>
            <button type="button" className="btn btn-round btn-xs btn-outline" onClick={() => dispatch(dismissRequest(session.Code, request))} aria-label={`Dismiss the request for ${request.SongTitle}`}>
              ×
            </button>
          </div>
        ))}
        <div className="sheet-note">Skips only affect today’s session. ♡ {session.Reactions}</div>
      </div>
      <div className="sheet-foot">
        <button type="button" className="btn btn-outline" onClick={onAdd}>
          Add a song
        </button>
        <button type="button" className="btn btn-end" onClick={end} disabled={ending}>
          {ending ? "Ending…" : "End session"}
        </button>
      </div>
    </>
  );
};

let searchSequence = 0;

/** Add a song by search (a tap on a row previews it); or paste, write or import one. */
const AddMode = ({ onBack, onPaste, onWrite, onImport, onPreview }) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const queued = useSelector((state) => state.songs);
  const queue = useSelector(selectQueueSongs);
  const favourites = useSelector((state) => state.library.songs);
  const topSongs = useSelector((state) => state.catalog.topSongs);
  const songKeys = useSelector((state) => state.preferences.songKeys);
  const { position, count } = useSelector(selectQueuePosition);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState({ term: "", songs: null }); // the last answer, for the term it answers

  useEffect(() => {
    dispatch(loadTopSongs());
  }, [dispatch]);

  const term = query.trim();
  useEffect(() => {
    if (!term) return undefined;
    const sequence = ++searchSequence;
    const handle = setTimeout(() => {
      catalogApi
        .search(term)
        .then((songs) => sequence === searchSequence && setFound({ term, songs }))
        .catch(() => sequence === searchSequence && setFound({ term, songs: [] }));
    }, 350);
    return () => clearTimeout(handle);
  }, [term]);

  const results = term && found.term === term ? found.songs : null;
  const seen = new Set();
  const suggestions = [...favourites, ...topSongs].filter((song) => !seen.has(song.Id) && seen.add(song.Id));
  const list = term ? results || [] : suggestions;

  const subFor = (song) => {
    const queuedSong = queued[song.Id];
    if (queuedSong) {
      const key = song.Key ? ` · ${transposeNote(song.Key, queuedSong.Transpose || 0)}` : "";
      if (song.Id === session.SongId) return `On screens now${key}`;
      return `Already ${ordinal(queue.findIndex((item) => item.Id === song.Id) + 1)}${key}`;
    }
    const key = song.Key ? `Adds in ${transposeNote(song.Key, songKeys[song.Id] || 0)}` : song.Artist || "";
    const lyricHit = term && !song.Title.toLowerCase().includes(term.toLowerCase());
    return `${key}${lyricHit ? (key ? " · " : "") + "lyric match" : ""}`;
  };

  const add = (song) => dispatch(queueSongNext(session.Code, song.Id, { atEnd: true, semitones: songKeys[song.Id] || 0 }));

  return (
    <>
      <div className="sheet-head">
        <button type="button" className="btn btn-round btn-xs btn-outline" onClick={onBack} aria-label="Back to the set">
          ←
        </button>
        <span className="sheet-title">Add a song</span>
        <span className="sheet-pos">{count ? `${position || 0} of ${count}` : ""}</span>
      </div>
      <div className="search" style={{ marginBottom: 12, flex: "none" }}>
        <span className="glyph" aria-hidden="true">
          ⌕
        </span>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Title, lyric, or writer" aria-label="Search songs" autoComplete="off" />
        {query && (
          <button type="button" className="clear" onClick={() => setQuery("")} aria-label="Clear the search">
            ×
          </button>
        )}
      </div>
      <div className="sheet-body">
        {list.map((song) => (
          <div key={song.Id} className="sheet-result">
            <PreviewRow title={song.Title} sub={subFor(song)} onPreview={(state) => onPreview(state ? { songId: song.Id, title: song.Title, held: state.held } : null)} />
            {queued[song.Id] ? (
              <span className="btn btn-xs btn-outline" aria-label="Already in the set">
                In set ✓
              </span>
            ) : (
              <button type="button" className="btn btn-xs btn-primary" onClick={() => add(song)} aria-label={`Add ${song.Title}`}>
                Add
              </button>
            )}
          </div>
        ))}
        {term && results && results.length === 0 && (
          <div className="sheet-note sheet-note-lg">Nothing matches. Paste the lyrics instead and it joins the set straight away.</div>
        )}
        {term && results === null && <div className="sheet-note sheet-note-lg">Searching…</div>}
        <div className="sheet-note">Added songs join today’s session only.</div>
      </div>
      <div className="sheet-foot">
        <button type="button" className="btn btn-outline" onClick={onPaste}>
          Paste lyrics
        </button>
        <button type="button" className="btn btn-outline" onClick={onWrite}>
          Write with chords
        </button>
        <button type="button" className="btn btn-outline" onClick={onImport}>
          From a link
        </button>
      </div>
    </>
  );
};

/** Paste plain lyrics: blank lines make sections; the song is saved and queued next. */
const PasteMode = ({ onBack, onDone }) => {
  const dispatch = useDispatch();
  const code = useSelector((state) => state.session.Code);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const blocks = parseSections(body);

  const save = async () => {
    if (!blocks.length || saving) return;
    setSaving(true);
    try {
      const created = await songsApi.addGuitarSong({ title: title.trim() || "Untitled song", lyrics: sectionsToText(body) });
      dispatch(addSongToLibrary(created.Id));
      await dispatch(queueSongNext(code, created.Id, { quiet: true }));
      dispatch(showInfo("Saved and queued next", { sub: created.Title }));
      onDone();
    } catch (error) {
      dispatch(showNotice(failureMessage(error, "Could not save the song, please try again")));
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sheet-head">
        <button type="button" className="btn btn-round btn-xs btn-outline" onClick={onBack} aria-label="Back">
          ←
        </button>
        <span className="sheet-title">Paste lyrics</span>
      </div>
      <input className="field field-serif" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Song title" aria-label="Song title" maxLength={100} style={{ flex: "none", marginBottom: 10 }} />
      <textarea
        className="field field-area"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        aria-label="Lyrics"
        placeholder={"Paste the lyrics here.\n\nLeave a blank line between each verse and chorus — that is how sections are found.\n\nStart a block with \"Chorus\" to name it."}
        style={{ flex: 1, minHeight: 120 }}
      />
      <div className="sheet-foot" style={{ flexDirection: "column" }}>
        {blocks.length === 0 ? (
          <div className="sheet-note">Blank lines split sections. Labels are guessed and you can rename them after.</div>
        ) : (
          <div style={{ width: "100%" }}>
            <div className="kicker kicker-accent" style={{ marginBottom: 8 }}>
              {blocks.length} {blocks.length === 1 ? "section" : "sections"} detected
            </div>
            <div className="preview-list">
              {blocks.map((block) => (
                <div key={block.id} className="preview-row">
                  <span className="preview-label" style={{ color: "#ffd75e" }}>
                    {block.label}
                  </span>
                  <span className="preview-text">{block.preview}</span>
                  <span className="preview-meta" style={{ color: "#a79e8c" }}>
                    {block.lineCount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <button type="button" className="btn btn-md btn-primary btn-block" onClick={save} disabled={!blocks.length || saving}>
          {saving ? "Saving…" : "Add to the set"}
        </button>
      </div>
    </>
  );
};

/**
 * The host's bottom sheet on the live page. Closed it shows where the set stands; open it
 * is the set strip, and from there a song can be added by search, paste, editor or link.
 * The handle (and the closed strip) toggle it with a tap and resize it with a drag.
 */
const HostSheet = ({ open, onOpenChange }) => {
  const dispatch = useDispatch();
  const code = useSelector((state) => state.session.Code);
  const [mode, setMode] = useState("set");
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null); // { songId, title, held }
  const [dragHeight, setDragHeight] = useState(null); // the sheet's height while it follows the finger
  const sheetRef = useRef(null);
  const collapsedHeight = useRef(null);

  const show = (next) => {
    setMode(next);
    setPreview(null);
    onOpenChange(true);
  };
  const close = () => {
    setMode("set");
    setDraft(null);
    setPreview(null);
    onOpenChange(false);
  };
  const toggle = () => (open ? close() : show("set"));
  const switchTo = (next) => {
    setPreview(null);
    setMode(next);
  };

  // Drag from the handle: the open content follows the finger between the closed and the
  // open height, and settles on the nearer one; a tap (no travel) just toggles.
  const onPointerDown = (event) => {
    if (event.button != null && event.button > 0) return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    if (!open) collapsedHeight.current = sheet.offsetHeight || collapsedHeight.current;
    const collapsed = collapsedHeight.current || COLLAPSED_FALLBACK;
    const opened = Math.max(collapsed, Math.min(window.innerHeight * OPEN_SHARE, OPEN_MAX));
    const startHeight = open ? sheet.offsetHeight || opened : collapsed;
    const startY = event.clientY || 0;
    let travelled = 0;
    let height = startHeight;
    setDragHeight(startHeight);
    const onMove = (move) => {
      const dy = (move.clientY || 0) - startY;
      travelled = Math.max(travelled, Math.abs(dy));
      height = Math.min(opened, Math.max(collapsed, startHeight - dy));
      setDragHeight(height);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      const shouldOpen = travelled < TAP_TOLERANCE ? !open : height > (collapsed + opened) / 2;
      setDragHeight(null);
      if (shouldOpen) {
        if (!open) show("set");
      } else if (open) close();
      else onOpenChange(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };
  // Enter or Space on the handle (no pointer involved) toggles outright
  const onClick = (event) => {
    if (event.detail === 0) toggle();
  };
  const drag = { onPointerDown, onClick };

  const queued = async (song, message) => {
    await dispatch(queueSongNext(code, song.Id, { quiet: true }));
    dispatch(showInfo(message, { sub: song.Title }));
    setDraft(null);
    setMode("set");
  };

  if (!open && dragHeight == null) return <ClosedSheet drag={drag} onAdd={() => show("add")} sheetRef={sheetRef} />;

  const dragging = dragHeight != null;
  const style = dragging ? { height: dragHeight, transition: "none" } : undefined;

  return (
    <>
      <div ref={sheetRef} className="sheet sheet-open" style={style} data-screen-label={`Host sheet · ${mode}`}>
        {mode === "set" && <SetMode drag={drag} onAdd={() => switchTo("add")} onPreview={setPreview} />}
        {mode === "add" && <AddMode onBack={() => switchTo("set")} onPaste={() => switchTo("paste")} onWrite={() => switchTo("edit")} onImport={() => switchTo("import")} onPreview={setPreview} />}
        {mode === "paste" && <PasteMode onBack={() => switchTo("add")} onDone={() => switchTo("set")} />}
        {mode === "edit" && (
          <SongEditorPage
            inSheet
            draft={draft}
            onCancel={() => {
              setDraft(null);
              switchTo("add");
            }}
            onSaved={(song) => queued(song, "Saved and queued next")}
          />
        )}
        {mode === "import" && (
          <SongImportPage
            inSheet
            onBack={() => switchTo("add")}
            onAdded={(song) => queued(song, "Imported and queued next")}
            onEdit={(form) => {
              setDraft(form);
              switchTo("edit");
            }}
          />
        )}
      </div>
      {preview && <SongPreview songId={preview.songId} title={preview.title} held={preview.held} onClose={() => setPreview(null)} />}
    </>
  );
};

export default HostSheet;
